<template>
  <div class="section-selector-container h-full" />
  <NModal :auto-focus="false" close-on-esc :on-esc="cancel" show to=".section-selector-container">
    <NCard
      :bordered="false"
      class="section-selector__card rounded-2 h-full"
      :content-style="{ overflowY: 'auto', backgroundColor: themeVars.primary6, borderRadius: '8px' }"
    >
      <NGrid class="h-full" cols="3" item-responsive responsive="screen">
        <NGridItem class="pa-2" span="3 m:2">
          <div ref="toolbarWrapper" class="sticky z-100">
            <template v-if="!shouldShowSmartFolderView">
              <div class="flex items-start gap-2">
                <CSearchFilters
                  v-model:filters="filters"
                  class="flex-1"
                  :disabled="!!isFetchingSections"
                  entity="canvas"
                  is-section
                  :placeholder="t('canvasUI.canvasBuilder.sectionSelector.searchByNameOrTag')"
                  :search-in-options="isSearchInEnabled ? searchInOptions : undefined"
                  v-bind="popularityProps"
                  @update:filters="fetchSectionsAfterFiltering"
                  @update:search="handleSearch"
                  @update:searchIn="handleUpdateSearchIn"
                />
                <CButton
                  v-if="showSmartFolderToggle"
                  v-tooltip="t('canvasUI.components.sections.CSectionManagementToolbar.smartFolders')"
                  circle
                  :disabled="!!isFetchingSections"
                  icon
                  @click="toggleSmartFoldersView"
                >
                  <CIcon icon="folder-bookmark" size="16px" />
                </CButton>
              </div>

              <SectionSelectorToolbar
                :added-item-ids="addedSections.map((s) => s.id)"
                :are-all-selected="areAllSelected"
                :are-some-selected="areSomeSelected"
                :current-page="currentPage"
                :disable-pagination="enablePipeMultisearch"
                :first-page-total-displayed-items="firstPageTotalDisplayedSections ?? 0"
                :loading="isFetchingSections"
                :page-size="pageSize"
                :selected-item-ids="selectedSections.map((s) => s.id)"
                :total-items="availableSectionsTotalCount"
                :total-selected="selectedSections?.length ?? 0"
                @clear-selection="selectedSections = []"
                @items-added="addItems"
                @items-removed="removeItems"
                @page-first="fetchFirstChunk"
                @page-last="fetchLastChunk"
                @page-next="fetchNextChunk"
                @page-previous="fetchPreviousChunk"
                @toggle-select-all="toggleSelectAll"
              />
            </template>
          </div>

          <template v-if="shouldShowSmartFolderView">
            <CSectionSmartFolders
              ref="smartFoldersRef"
              :added-section-ids="addedSections.map((s) => s.id)"
              mode="selector"
              :selected-section-ids="selectedSections.map((s) => s.id)"
              storage-key="section-selector"
              @clear-selection="selectedSections = []"
              @section-add="handleSmartFolderSectionAdd"
              @section-remove="handleSmartFolderSectionRemove"
              @section-select="handleSmartFolderSectionSelect"
              @sections-bulk-add="handleSmartFolderBulkAdd"
              @sections-bulk-remove="handleSmartFolderBulkRemove"
            />
          </template>
          <template v-else>
            <div v-if="isFetchingSections" class="pt-3 overflow-auto" :style="{ height: wrapperHeight }">
              <NSkeleton v-for="n in 8" :key="n" class="w-full rounded" :class="{ 'mt-1': n > 1 }" :height="95" />
            </div>
            <CList
              v-else
              class="rounded-b-lg flex bg-base overflow-y-auto"
              :data="orderedAvailableSections"
              :height="parseInt(wrapperHeight)"
              :maxRowHeight="200"
              movable
              :selectedIds="selectedSections.map((s) => s.id)"
              @item:click="onSectionSelect"
            >
              <template #item="{ item, index, isSelected }">
                <SectionSelectorItem
                  :data="orderedAvailableSections"
                  :filters="filters"
                  :has-sequence="sequenceEnabled"
                  :index="index"
                  :is-selected="isSelected"
                  :item="item"
                  :selected-sections="selectedSections"
                >
                  <template #actions>
                    <CButton
                      v-if="launchDarkly.enable_section_selector_edit_section && isAdmin"
                      v-tooltip="t('canvasUI.canvasBuilder.sectionSelector.openSectionInEditMode')"
                      circle
                      class="h-9 w-9 absolute right-14 opacity-0 group-hover:opacity-100 transition-opacity"
                      secondary
                      type="default"
                      @click.stop="navigateToSectionEdit(getTypedItem(item))"
                    >
                      <template #icon>
                        <CIcon class="ml-[2px] color-primary" icon="arrow-up-right-from-square" size="12" />
                      </template>
                    </CButton>
                    <CButton
                      circle
                      class="h-9 w-9"
                      :class="{
                        'bg-base !focus:bg-base !active:bg-base': !isSelected && !isAdded(getTypedItem(item)),
                        'bg-primary5': isSelected,
                        'bg-error2 !focus:bg-error2 !active:bg-error2 ': isAdded(getTypedItem(item)),
                      }"
                      secondary
                      type="default"
                      @click.stop="handleItemClick(getTypedItem(item))"
                    >
                      <template #icon>
                        <CIcon v-if="isSelected" class="ml-[2px] color-primary" icon="grip-dots-vertical" size="12" />
                        <CIcon
                          v-else-if="isAdded(getTypedItem(item))"
                          class="ml-[2px] color-error"
                          icon="times"
                          size="12"
                        />
                        <CIcon v-else class="ml-[2px] color-primary" icon="plus" size="12" />
                      </template>
                    </CButton>
                  </template>
                </SectionSelectorItem>
              </template>
            </CList>
          </template>
        </NGridItem>
        <NGridItem class="pa-2" span="3 m:1">
          <SectionSelectorSelected
            ref="sectionSelectorSelectedRef"
            v-model="addedSections"
            @clear="addedSections = []"
            @drop="onDrop"
            @remove="onRemoveSelection"
          />
        </NGridItem>
      </NGrid>

      <template #footer>
        <div class="flex gap-2 justify-end mt-2 relative top-1">
          <CButton :disabled="isSavingCanvas" @click="emit('cancel')">
            {{ cancelText || t('canvasUI.common.cancel') }}
          </CButton>
          <CButton :disabled="isDoneDisabled" :loading="isSavingCanvas" type="primary" @click="handleDone">
            {{ doneText || t('canvasUI.common.done') }}
          </CButton>
        </div>
      </template>
    </NCard>
  </NModal>
</template>
<script setup lang="ts">
import { orderBy, sortBy, unionBy, uniq } from 'lodash-es'
import { NModal, NCard, NGrid, NGridItem, NSkeleton } from 'naive-ui'
import { computed, inject, nextTick, onMounted, ref, type ComputedRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AxiosInstance } from 'axios'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CList from '@lib/components/CList/CList.vue'

import SectionSelectorItem from './SectionSelectorItem.vue'
import SectionSelectorToolbar from './SectionSelectorToolbar.vue'
import { fetchSectionsWithPipeMultiSearch } from './SectionSelector.helpers'
import CSearchFilters from '@lib/components/CSearchFilters/CSearchFilters.vue'
import type { SearchInOption } from '@lib/components/CSearchFilters/CSearchFilters.types'
import SectionSelectorSelected from '@canvas-builder/components/ui/SectionList/SectionSelectorSelected.vue'
import CSectionSmartFolders from '@lib/components/CSectionSmartFolders/CSectionSmartFolders.vue'
import useConfirmation from '@lib/composables/useConfirmation'
import useSectionSmartFolderStore from '@lib/stores/sectionSmartFolderStore'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import { usePopularity, EntityType } from '@canvas-builder/composables/usePopularity'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { useElementHeightResize } from '@lib/composables/useElementHeightResize'
import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import type { CanvasBuilderProps, SectionListProps } from '@canvas-builder/types/canvas'
import type { CanvasRetrieve, Canvas } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { PitcherInfo } from '@lib/types/app'

const props = withDefaults(
  defineProps<{
    isSavingCanvas: boolean
    selectionStrategy?: SectionListProps['selection_strategy']
    selectedSectionIds: CanvasRetrieve['id'][]
    cancelText?: string
    doneText?: string
  }>(),
  {
    selectionStrategy: (props) => props.selectionStrategy || 'free',
  },
)

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'select', items: CanvasRetrieve[]): void
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)
const openSection = inject<{
  (canvasId: CanvasRetrieve['id'], { editMode }: { editMode?: boolean }): void
}>('open-section')
const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios')
const pageSize = computed(() => launchDarkly.value.canvas_section_selector_page_size || 500)
const filters = ref<Record<string, any>>({})
const { engagingSections, getOrderingConfiguration, getSystemFilterSectionIds, popularSections, getDefaultOrdering } =
  useSectionPopularity(computed(() => filters.value))
const { fetchPopularityData } = usePopularity()
const { t } = useI18n()
const listCanvases = inject<CanvasBuilderProps['listCanvases']>('listCanvases')
const search = ref('')
const sectionSelectorSelectedRef = ref<InstanceType<typeof SectionSelectorSelected> | null>(null)
const smartFoldersRef = ref<InstanceType<typeof CSectionSmartFolders> | null>(null)
const addedSections = ref<CanvasRetrieve[]>([])
const availableSections = ref<CanvasRetrieve[]>([])
const availableSectionsTotalCount = ref(0)
const selectedSections = ref<CanvasRetrieve[]>([])
const isFetchingSections = ref(false)
const isFetchingSelectedSections = ref(false)
const currentPage = ref(1)
const firstPageTotalDisplayedSections = ref<number | undefined>()
const toolbarWrapper = ref<HTMLElement | null>(null)
const { wrapperHeight } = useElementHeightResize(toolbarWrapper, calculateWrapperHeight)
const { recommendedSectionIds } = useRecommendations()
const { isAdmin } = useCanvas()
const themeVars = useThemeVars()
const confirmation = useConfirmation()
const { isSmartFoldersView } = useSectionSmartFolderStore()

const isSmartFoldersEnabled = computed(
  () =>
    launchDarkly.value.enable_smart_folders_for_sections && launchDarkly.value.enable_smart_folders_in_section_selector,
)

const shouldShowSmartFolderView = computed(() => isSmartFoldersEnabled.value && isSmartFoldersView.value)

const showSmartFolderToggle = computed(() => isSmartFoldersEnabled.value)

const isDoneDisabled = computed(() => {
  if (pitcherInfo.value?.platform === 'admin') return false

  switch (props.selectionStrategy) {
    case 'single':
      return addedSections.value.length !== 1
    case 'at-least-one':
      return addedSections.value.length < 1
    case 'app':
    default:
      return false
  }
})

const isSearchInEnabled = computed(() => launchDarkly.value.enable_insearch_in_section_selector)
const searchInOptions: SearchInOption[] = [
  {
    label: t('canvasUI.canvasBuilder.sectionSelector.searchInAll'),
    value: 'name,tags,metadata,text',
    default: true,
  },
  {
    label: t('canvasUI.canvasBuilder.sectionSelector.searchInFileName'),
    value: 'name',
  },
  {
    label: t('canvasUI.canvasBuilder.sectionSelector.searchInTags'),
    value: 'tags',
  },
  {
    label: t('canvasUI.canvasBuilder.sectionSelector.searchInMetadata'),
    value: 'metadata',
  },
]
let searchIn = isSearchInEnabled.value ? searchInOptions[0].value : undefined

const popularityProps = computed(() => ({
  engagingIds: launchDarkly.value.show_popularity_icons ? engagingSections.value.map((item) => item.id) : undefined,
  popularIds: launchDarkly.value.show_popularity_icons ? popularSections.value.map((item) => item.id) : undefined,
  recommendedIds: recommendedSectionIds.value ?? [],
  showLatestFilter: true,
}))

const enablePipeMultisearch = computed(() => !!(launchDarkly.value.enable_pipe_character_multi_search && search.value))
const pipeSearchTerms = computed(() =>
  uniq(
    search.value
      .split(pitcherSettings.value?.pipe_multi_search_separator || '|')
      .map((term) => term.trim())
      .filter((term) => term),
  ),
)
const isPipeSearching = computed(() => pipeSearchTerms.value.length > 1)
const orderedAvailableSections = computed(() => {
  if (enablePipeMultisearch.value && isPipeSearching.value) {
    return availableSections.value
  }

  const sorters = getOrderingConfiguration({ sortByMetadata: true })

  return orderBy(
    availableSections.value,
    sorters.map(([sorter]) => sorter),
    sorters.map(([, order]) => order),
  )
})

const sequenceEnabled = computed(() => {
  return orderedAvailableSections.value.some((item) => item.metadata?.sequence)
})

function navigateToSectionEdit(item: CanvasRetrieve) {
  openSection?.(item.id.toString(), { editMode: true })
}

function onDrop(dropOnItemId?: Canvas['id'], index?: number) {
  addItems(
    selectedSections.value.map((i) => i.id),
    'bottom',
    index,
  )
}

const areAllSelected = computed(() =>
  availableSections.value.every((section) => selectedSections.value.some((s) => s.id === section.id)),
)
const areSomeSelected = computed(
  () =>
    availableSections.value.some((section) => selectedSections.value.some((s) => s.id === section.id)) &&
    !areAllSelected.value,
)

function getTypedItem(item: any) {
  return item as CanvasRetrieve
}

function isAdded(item: CanvasRetrieve) {
  return addedSections.value.some((s) => s.id === item.id)
}

function calculateWrapperHeight(height: number) {
  return `${(toolbarWrapper.value?.parentElement?.parentElement?.offsetHeight || 0) - (height || 0) - 20}px`
}

function onSectionSelect(section: CanvasRetrieve) {
  const elementExists = selectedSections.value.find((s) => s.id === section.id)
  if (elementExists) selectedSections.value = selectedSections.value.filter((s) => s.id !== section.id)
  else selectedSections.value.push(section)
}

function onRemoveSelection(section: Canvas) {
  addedSections.value = addedSections.value.filter((s) => s.id !== section.id)
}

function selectChunk() {
  selectedSections.value = [
    ...selectedSections.value,
    ...orderedAvailableSections.value.filter(
      (s) => !selectedSections.value.some((selectedSection) => selectedSection.id === s.id),
    ),
  ]
}

async function fetchSections() {
  selectedSections.value = []
  const ordering = getDefaultOrdering()
  if (enablePipeMultisearch.value) {
    try {
      isFetchingSections.value = true
      const sectionIds = getSystemFilterSectionIds()

      const result = await fetchSectionsWithPipeMultiSearch({
        listCanvases,
        search: search.value,
        pipeSearchTerms: pipeSearchTerms.value,
        isPipeSearching: isPipeSearching.value,
        pageSize: pageSize.value,
        currentPage: currentPage.value,
        filters: filters.value,
        sectionIds,
        pitcherSettings: pitcherSettings.value,
        searchIn: searchIn,
        restApiAxios: restApiAxios?.value,
      })

      availableSections.value = result.availableSections
      availableSectionsTotalCount.value = result.availableSectionsTotalCount
      firstPageTotalDisplayedSections.value = result.firstPageTotalDisplayedSections
    } catch (e) {
      console.error('Failed to fetch sections', e)
    } finally {
      isFetchingSections.value = false
    }
    return
  }

  if (!listCanvases) return

  isFetchingSections.value = true
  try {
    availableSections.value = []

    const sectionIds = getSystemFilterSectionIds()

    // only check for null value not empty array
    if (!sectionIds) {
      availableSectionsTotalCount.value = 0
      isFetchingSections.value = false
      return
    }

    const requestPromises = [
      listCanvases({
        is_section: true,
        is_template: false,
        is_draft: false,
        ordering,
        page_size: pageSize.value,
        search: search.value,
        page: currentPage.value,
        filters: normalizeFilterParams(filters.value),
        id__in: sectionIds?.length ? sectionIds.slice(0, pageSize.value).join(',') : undefined,
        search_in: searchIn,
      }),
    ]

    // only show all recommended sections if on the first page
    if (recommendedSectionIds.value?.length && currentPage.value === 1 && !sectionIds?.length)
      requestPromises.push(
        listCanvases({
          is_section: true,
          is_template: false,
          is_draft: false,
          ordering,
          search: search.value,
          page_size: pageSize.value,
          filters: normalizeFilterParams(filters.value),
          id__in: recommendedSectionIds.value.join(','),
          search_in: searchIn,
        }),
      )

    const [sectionsResponse, recommendedSectionsResponse] = await Promise.all(requestPromises)

    if (recommendedSectionsResponse && recommendedSectionsResponse.results?.length) {
      availableSectionsTotalCount.value = sectionsResponse.count

      const sectionsWithAllRecommendedSections = unionBy(
        sectionsResponse.results,
        recommendedSectionsResponse.results,
        'id',
      )
      firstPageTotalDisplayedSections.value = sectionsWithAllRecommendedSections.length
      availableSections.value = sectionsWithAllRecommendedSections
    } else {
      firstPageTotalDisplayedSections.value = undefined
      availableSectionsTotalCount.value = sectionsResponse.count
      availableSections.value = sectionsResponse.results
    }
  } catch (e) {
    console.error('Failed to fetch sections', e)
  } finally {
    isFetchingSections.value = false
  }
}

async function fetchSelectedSections() {
  if (!listCanvases) return
  const ordering = getDefaultOrdering()
  isFetchingSelectedSections.value = true
  try {
    // Split the selectedSectionIds into chunks safe enough for GET URLs
    const maxIdsPerChunk = 100 // Adjust the chunk size to be safe for GET requests
    const idChunks = []
    for (let i = 0; i < props.selectedSectionIds.length; i += maxIdsPerChunk) {
      idChunks.push(props.selectedSectionIds.slice(i, i + maxIdsPerChunk))
    }

    // Fetch sections for each chunk and combine the results
    const sectionsPromises = idChunks.map((chunk) =>
      listCanvases({
        is_section: true,
        is_template: false,
        ordering,
        page_size: pageSize.value,
        id__in: chunk.join(','),
        search_in: searchIn,
      }),
    )
    const sectionsResults = await Promise.all(sectionsPromises)
    const flattenResults = sectionsResults.flatMap((res) => res.results)

    if (launchDarkly.value.enable_selectors_dnd)
      addedSections.value = sortBy(flattenResults, (value) => props.selectedSectionIds.indexOf(value.id))
    else selectedSections.value = sortBy(flattenResults, (value) => props.selectedSectionIds.indexOf(value.id))
  } catch (e) {
    console.error('Failed to fetch sections', e)
  } finally {
    isFetchingSelectedSections.value = false
  }
}

function fetchNextChunk() {
  currentPage.value++
  fetchSections()
}

function fetchPreviousChunk() {
  if (currentPage.value > 1) {
    currentPage.value--
    fetchSections()
  }
}

function fetchFirstChunk() {
  currentPage.value = 1
  fetchSections()
}

function fetchLastChunk() {
  if (availableSectionsTotalCount.value > 0) {
    currentPage.value = Math.ceil(availableSectionsTotalCount.value / pageSize.value)
    fetchSections()
  }
}

function fetchSectionsAfterFiltering() {
  currentPage.value = 1
  fetchSections()
}

function handleSearch(newValue: string) {
  search.value = newValue
  currentPage.value = 1
  fetchSections()
}

function handleUpdateSearchIn(newValue: string) {
  searchIn = newValue
  if (search.value) {
    currentPage.value = 1
    fetchSections()
  }
}

function toggleSelectAll() {
  if (areAllSelected.value)
    selectedSections.value = selectedSections.value.filter(
      (s) => !availableSections.value.some((section) => s.id === section.id),
    )
  else selectChunk()
}

function addItems(itemIds: CanvasRetrieve['id'][], position?: 'top' | 'bottom', targetIndex?: number) {
  const itemsToMove = addedSections.value.filter((section) => itemIds.includes(section.id))
  let newItemsToAdd = availableSections.value.filter(
    (s) => itemIds.includes(s.id) && !itemsToMove.some((m) => m.id === s.id),
  )

  // If we're in smart folders view and some items weren't found in availableSections,
  // try to get them from smart folders
  if (shouldShowSmartFolderView.value && smartFoldersRef.value) {
    const smartFolderSections = smartFoldersRef.value.getSelectedSections()
    const missingIds = itemIds.filter(
      (id) => !newItemsToAdd.some((item) => item.id === id) && !itemsToMove.some((item) => item.id === id),
    )

    const smartFolderItemsToAdd = smartFolderSections.filter((section) => missingIds.includes(section.id))
    newItemsToAdd = [...newItemsToAdd, ...smartFolderItemsToAdd]
  }

  if (targetIndex === undefined && newItemsToAdd.length === 0 && itemsToMove.length === 0) {
    return
  }

  let adjustedIndex = targetIndex
  const remainingItems = addedSections.value.filter((section) => {
    if (itemIds.includes(section.id)) {
      if (targetIndex !== undefined && addedSections.value.indexOf(section) < targetIndex) {
        if (adjustedIndex !== undefined) {
          adjustedIndex--
        }
      }
      return false
    }
    return true
  })

  let itemsToAdd = itemIds
    .map((id) => itemsToMove.find((item) => item.id === id) || newItemsToAdd.find((item) => item.id === id))
    .filter((item): item is CanvasRetrieve => item !== undefined)

  // Sort itemsToAdd based on their order in orderedAvailableSections if sequenceEnabled is true
  if (sequenceEnabled.value) {
    itemsToAdd = sortBy(itemsToAdd, (item) =>
      orderedAvailableSections.value.findIndex((section) => section.id === item.id),
    )
  }

  if (position === 'top') {
    addedSections.value = [...itemsToAdd, ...remainingItems]
  } else if (adjustedIndex !== undefined) {
    addedSections.value = [
      ...remainingItems.slice(0, adjustedIndex),
      ...itemsToAdd,
      ...remainingItems.slice(adjustedIndex),
    ]
  } else {
    addedSections.value = [...remainingItems, ...itemsToAdd]
  }

  selectedSections.value = []

  nextTick(() => {
    const container = sectionSelectorSelectedRef.value?.$el
    if (!container) return

    const containerRect = container.getBoundingClientRect()

    if (targetIndex !== undefined) {
      const targetElement = container.children[targetIndex]
      if (targetElement) {
        const targetRect = targetElement.getBoundingClientRect()
        const isDropInVisibleArea = targetRect.top >= containerRect.top && targetRect.bottom <= containerRect.bottom

        if (!isDropInVisibleArea) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }
    } else if (position) {
      container.scrollTo({
        top: position === 'top' ? 0 : container.scrollHeight,
        behavior: 'smooth',
      })
    }
  })
}

function handleItemClick(item: CanvasRetrieve) {
  if (isAdded(item)) {
    removeItems([item.id])
  } else {
    addItems([item.id])
  }
}

function removeItems(itemIds: CanvasRetrieve['id'][]) {
  addedSections.value = itemIds?.length
    ? addedSections.value.filter((s) => !itemIds.some((itemId) => s.id === itemId))
    : []
  selectedSections.value = []
}

function toggleSmartFoldersView() {
  isSmartFoldersView.value = !isSmartFoldersView.value

  selectedSections.value = []

  // Recalculate height when switching to smart folders view
  if (isSmartFoldersView.value) {
    nextTick(() => {
      smartFoldersRef.value?.calculateContentHeight?.()
    })
  }
}

function handleSmartFolderSectionSelect(section: CanvasRetrieve) {
  const elementExists = selectedSections.value.find((s) => s.id === section.id)
  if (elementExists) {
    selectedSections.value = selectedSections.value.filter((s) => s.id !== section.id)
  } else {
    selectedSections.value.push(section)
  }
}

function handleSmartFolderSectionAdd(section: CanvasRetrieve) {
  addSectionToList([section])
}

function handleSmartFolderSectionRemove(section: CanvasRetrieve) {
  removeItems([section.id])
}

function addSectionToList(sections: CanvasRetrieve[], position?: 'top' | 'bottom', targetIndex?: number) {
  const sectionIds = sections.map((s) => s.id)

  // Separate existing items from new items (following the same logic as addItems)
  const itemsToMove = addedSections.value.filter((section) => sectionIds.includes(section.id))
  const newItemsToAdd = sections.filter(
    (s) => !itemsToMove.some((m) => m.id === s.id) && !addedSections.value.some((added) => added.id === s.id),
  )

  if (targetIndex === undefined && newItemsToAdd.length === 0 && itemsToMove.length === 0) {
    return
  }

  let adjustedIndex = targetIndex
  const remainingItems = addedSections.value.filter((section) => {
    if (sectionIds.includes(section.id)) {
      if (targetIndex !== undefined && addedSections.value.indexOf(section) < targetIndex) {
        if (adjustedIndex !== undefined) {
          adjustedIndex--
        }
      }
      return false
    }
    return true
  })

  let itemsToAdd = sectionIds
    .map((id) => itemsToMove.find((item) => item.id === id) || newItemsToAdd.find((item) => item.id === id))
    .filter((item): item is CanvasRetrieve => item !== undefined)

  // Sort itemsToAdd based on their order in smart folders if sequenceEnabled is true
  if (sequenceEnabled.value && smartFoldersRef.value) {
    itemsToAdd = sortBy(itemsToAdd, (item) => {
      const smartFolderSections = smartFoldersRef.value?.getSelectedSections() || []
      return smartFolderSections.findIndex((section) => section.id === item.id)
    })
  }

  if (position === 'top') {
    addedSections.value = [...itemsToAdd, ...remainingItems]
  } else if (adjustedIndex !== undefined) {
    addedSections.value = [
      ...remainingItems.slice(0, adjustedIndex),
      ...itemsToAdd,
      ...remainingItems.slice(adjustedIndex),
    ]
  } else {
    addedSections.value = [...remainingItems, ...itemsToAdd]
  }

  // Clear selection
  selectedSections.value = []

  // Scroll to show the added sections
  nextTick(() => {
    const container = sectionSelectorSelectedRef.value?.$el
    if (!container) return

    const containerRect = container.getBoundingClientRect()

    if (adjustedIndex !== undefined) {
      const targetElement = container.children[adjustedIndex]
      if (targetElement) {
        const targetRect = targetElement.getBoundingClientRect()
        const isDropInVisibleArea = targetRect.top >= containerRect.top && targetRect.bottom <= containerRect.bottom

        if (!isDropInVisibleArea) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }
    } else if (position) {
      container.scrollTo({
        top: position === 'top' ? 0 : container.scrollHeight,
        behavior: 'smooth',
      })
    }
  })
}

function handleSmartFolderBulkAdd(sections: CanvasRetrieve[], position?: 'top' | 'bottom', targetIndex?: number) {
  addSectionToList(sections, position, targetIndex)
}

function handleSmartFolderBulkRemove(sections: CanvasRetrieve[]) {
  const sectionIds = sections.map((s) => s.id)
  removeItems(sectionIds)
}

function handleDone() {
  // check if someone forgot to add sections
  if (
    selectedSections.value.length > 0 &&
    selectedSections.value.some(
      (selectedSection) => !addedSections.value.some((addedSection) => addedSection.id === selectedSection.id),
    )
  ) {
    confirmation.warning({
      hasExit: false,
      title: t('canvasUI.canvasBuilder.sectionSelector.leaveDialog.title'),
      content: t('canvasUI.canvasBuilder.sectionSelector.leaveDialog.content'),
      confirmText: t('canvasUI.common.ok'),
      cancelText: t('canvasUI.common.cancel'),
      onConfirm: () => emit('select', addedSections.value),
    })
  } else {
    emit('select', addedSections.value)
  }
}

onMounted(() => {
  if (launchDarkly.value.show_popularity_icons) {
    fetchPopularityData(EntityType.SECTION)
  }

  fetchSections()
  fetchSelectedSections()
})

const cancel = () => {
  emit('cancel')
}
</script>

<style scoped lang="scss">
.section-selector-container {
  font-family: var(--font-family);

  :deep(.n-modal-scroll-content) {
    height: 100%;
    padding: 24px;
  }
}
</style>
